import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getCoachByToken, submitCoachProfile } from "@/lib/coaches.functions";
import { INDUSTRIES, FUNCTIONS, PHASES } from "@/lib/coach-options";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/coach-formular/$token")({
  loader: ({ params }) => getCoachByToken({ data: { token: params.token } }),
  component: CoachForm,
  errorComponent: ({ error }) => (
    <Wrapper><div className="text-sm text-destructive">{error.message}</div></Wrapper>
  ),
});

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">{children}</div>
    </div>
  );
}

function CoachForm() {
  const result = Route.useLoaderData();
  const { token } = Route.useParams();
  const submit = useServerFn(submitCoachProfile);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!result.ok) {
    return (
      <Wrapper>
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <h1 className="font-display text-2xl mb-2">Länken är inte giltig</h1>
          <p className="text-muted-foreground text-sm">
            {result.reason === "expired" ? "Den här länken har gått ut. Be om en ny från Inkubera." : "Vi hittar ingen profil för den här länken."}
          </p>
        </div>
      </Wrapper>
    );
  }

  const c = result.coach;
  const [fullName, setFullName] = useState(c.full_name);
  const [phone, setPhone] = useState(c.phone ?? "");
  const [city, setCity] = useState(c.city ?? "");
  const [languages, setLanguages] = useState((c.languages ?? []).join(", "));
  const [linkedin, setLinkedin] = useState(c.linkedin_url ?? "");
  const [bio, setBio] = useState(c.bio ?? "");
  const [prev, setPrev] = useState(c.previous_companies ?? "");
  const [industries, setIndustries] = useState<string[]>(c.industries ?? []);
  const [functions, setFunctions] = useState<string[]>(c.functions ?? []);
  const [phases, setPhases] = useState<string[]>(c.business_phases ?? []);
  const [photo, setPhoto] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(c.photo_url);

  function handlePhoto(file: File) {
    if (file.size > 5 * 1024 * 1024) { toast.error("Bilden är för stor (max 5 MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      setPhoto(data);
      setPhotoPreview(data);
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await submit({
        data: {
          token,
          full_name: fullName.trim(),
          phone: phone.trim() || null,
          city: city.trim() || null,
          languages: languages.split(",").map((s: string) => s.trim()).filter(Boolean),
          linkedin_url: linkedin.trim() || null,
          bio: bio.trim() || null,
          previous_companies: prev.trim() || null,
          industries, functions, business_phases: phases,
          photo_data_url: photo,
        },
      });
      setDone(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte spara.");
    } finally {
      setSaving(false);
    }
  }

  if (done) {
    return (
      <Wrapper>
        <div className="rounded-2xl border border-border bg-card p-10 text-center">
          <CheckCircle2 className="size-12 mx-auto text-foreground mb-4" />
          <h1 className="font-display text-2xl mb-2">Tack!</h1>
          <p className="text-muted-foreground text-sm">Din profil är sparad. Du kan stänga den här fliken.</p>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Inkubera · Affärscoach</div>
        <h1 className="font-display text-3xl">Din kompetensprofil</h1>
        <p className="text-muted-foreground text-sm mt-2">Hej {c.full_name.split(" ")[0]}! Fyll i din profil så vi kan matcha dig mot rätt bolag.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card title="Profilbild">
          <div className="flex items-center gap-4">
            {photoPreview ? <img src={photoPreview} alt="" className="size-20 rounded-full object-cover" /> : <div className="size-20 rounded-full bg-secondary" />}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => e.target.files?.[0] && handlePhoto(e.target.files[0])} className="text-sm" />
          </div>
        </Card>

        <Card title="Kontakt">
          <Field label="Namn"><Input value={fullName} onChange={(e) => setFullName(e.target.value)} required /></Field>
          <Field label="Telefon"><Input value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
          <Field label="Ort"><Input value={city} onChange={(e) => setCity(e.target.value)} /></Field>
          <Field label="LinkedIn"><Input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/..." /></Field>
          <Field label="Språk (kommaseparerat)"><Input value={languages} onChange={(e) => setLanguages(e.target.value)} placeholder="Svenska, Engelska" /></Field>
        </Card>

        <Card title="Om dig">
          <Field label="Kort bio"><Textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)} maxLength={2000} /></Field>
          <Field label="Tidigare bolag och roller"><Textarea rows={4} value={prev} onChange={(e) => setPrev(e.target.value)} maxLength={2000} /></Field>
        </Card>

        <Card title="Branscher"><TagPicker options={INDUSTRIES} value={industries} onChange={setIndustries} /></Card>
        <Card title="Funktionella områden"><TagPicker options={FUNCTIONS} value={functions} onChange={setFunctions} /></Card>
        <Card title="Affärsfaser"><TagPicker options={PHASES} value={phases} onChange={setPhases} /></Card>

        <Button type="submit" className="w-full" disabled={saving}>{saving ? "Sparar…" : "Spara profil"}</Button>
      </form>
    </Wrapper>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
function TagPicker({ options, value, onChange }: { options: readonly string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button key={o} type="button"
            onClick={() => onChange(active ? value.filter((v) => v !== o) : [...value, o])}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}
          >{o}</button>
        );
      })}
    </div>
  );
}
